import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Title} from '@angular/platform-browser';
import {TranslocoService} from '@jsverse/transloco';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {DOCUMENT_TITLE_DIVIDER, DOCUMENT_TITLE_SUFFIX} from '../constants';
import {PageTitle} from '../../interfaces/page-title.interface';

@Injectable({
  providedIn: 'root',
})
export class PageService {
  private readonly titleService = inject(Title);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private currentPageTitle$ = new BehaviorSubject<PageTitle>({key: 'home.title', params: {}});
  private documentPageTitle$ = new BehaviorSubject<PageTitle>({
    key: 'global.application.pageTitle',
    params: {},
  });
  private showBackLink$ = new BehaviorSubject<boolean>(false);

  constructor() {
    combineLatest([this.translocoService.langChanges$, this.documentPageTitle$])
      .pipe(
        switchMap(([_, pageTitle]) =>
          this.translocoService.selectTranslate(pageTitle.key, pageTitle.params),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((translated: string) => {
        const extendedTitle = `${translated} ${DOCUMENT_TITLE_DIVIDER} ${DOCUMENT_TITLE_SUFFIX}`;
        this.titleService.setTitle(extendedTitle);
      });
  }

  getShowBackLink() {
    return this.showBackLink$.asObservable();
  }

  setShowBackLink(showBackLink: boolean) {
    this.showBackLink$.next(showBackLink);
  }

  getCurrentPageTitle() {
    return this.currentPageTitle$.asObservable();
  }

  setCurrentPageTitle(pageTitle: PageTitle) {
    this.currentPageTitle$.next(pageTitle);
  }

  setDocumentPageTitle(documentPageTitle: PageTitle) {
    this.documentPageTitle$.next(documentPageTitle);
  }
}
